import { useCallback, useEffect } from 'react';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  UseFormClearErrors,
  UseFormSetError
} from 'react-hook-form';
import Spinner from 'src/components/atoms/Spinner';
import { SpinnerColor, SpinnerSize } from 'src/components/atoms/Spinner/Spinner';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { isValidSalesforceId } from 'src/helpers';
import { useDebounce } from 'src/hooks';
import useCheckCompanySalesforceId from './useCheckCompanySalesforceId';

/**
 * Configuration options for useSalesforceIdField hook
 */
interface UseSalesforceIdFieldOptions<TFieldValues extends FieldValues> {
  /** React Hook Form control instance */
  control: Control<TFieldValues>;
  /** Field name/path for the Salesforce Account ID field */
  fieldName: FieldPath<TFieldValues>;
  /** Function to set form validation errors */
  setError: UseFormSetError<TFieldValues>;
  /** Function to clear form validation errors */
  clearErrors: UseFormClearErrors<TFieldValues>;
  /**
   * Original value for edit scenarios. When provided, availability check
   * is only triggered if the current value differs from the original.
   */
  originalValue?: string | null;
  /**
   * Whether the feature is enabled. When false, the hook returns no-op values.
   * @default false
   */
  enabled?: boolean;
}

/**
 * Return type for useSalesforceIdField hook
 */
interface UseSalesforceIdFieldResult {
  /** React element to render as field indicator (spinner, checkmark, error icon, etc.) */
  indicator: React.ReactNode;
  /** Whether the availability check is currently in progress */
  isLoading: boolean;
}

/**
 * Custom hook that manages Salesforce Account ID field validation and availability checking.
 *
 * This hook encapsulates the logic for:
 * - Debounced availability checking
 * - Visual indicator rendering (loading, success, error states)
 * - Form error management based on availability
 * - Optional comparison with original value (for edit scenarios)
 *
 * @example
 * // Registration form (no original value)
 * const { indicator, isLoading } = useSalesforceIdField({
 *   control,
 *   fieldName: 'company.salesforce_account_id',
 *   setError,
 *   clearErrors
 * });
 *
 * @example
 * // Edit form (with original value)
 * const { indicator, isLoading } = useSalesforceIdField({
 *   control,
 *   fieldName: 'company.salesforce_account_id',
 *   setError,
 *   clearErrors,
 *   originalValue: company.salesforce_account_id
 * });
 */
export function useSalesforceIdField<TFieldValues extends FieldValues>({
  control,
  fieldName,
  setError,
  clearErrors,
  originalValue,
  enabled = false
}: UseSalesforceIdFieldOptions<TFieldValues>): UseSalesforceIdFieldResult {
  // Use useController for targeted field subscription (prevents unnecessary re-renders)
  const { field } = useController({
    name: fieldName,
    control
  });

  // Check Salesforce Account ID availability
  const {
    checkSalesforceAccountId,
    isLoading: isSalesforceAccountIdCheckLoading,
    isAvailable: isSalesforceAccountIdAvailable,
    error: salesforceAccountIdCheckError
  } = useCheckCompanySalesforceId();

  const debouncedValue = useDebounce(field.value, 500);

  // Trigger availability check on debounced input (only when enabled)
  useEffect(() => {
    if (!enabled) return;

    const hasValue = debouncedValue && debouncedValue.length > 0;
    const isDifferentFromOriginal =
      originalValue !== undefined ? debouncedValue !== originalValue : true;

    if (hasValue && isDifferentFromOriginal) {
      checkSalesforceAccountId(debouncedValue);
    }
  }, [debouncedValue, checkSalesforceAccountId, originalValue, enabled]);

  // Helper function to determine the indicator based on availability state
  const getIndicator = useCallback((): React.ReactNode => {
    // Return null if feature is disabled
    if (!enabled) {
      return null;
    }

    const currentValue = field.value;
    const isValid = isValidSalesforceId(currentValue ?? '');

    // For edit scenarios, don't show indicator if value matches original
    const matchesOriginal = originalValue !== undefined && currentValue === originalValue;

    // Don't show indicator if invalid format, empty, or matches original
    if (!isValid || !currentValue || matchesOriginal) {
      return null;
    }

    // Show loading spinner while checking
    if (isSalesforceAccountIdCheckLoading) {
      return (
        <div data-testid="salesforce-id-spinner">
          <Spinner size={SpinnerSize.small} color={SpinnerColor.primary} centered />
        </div>
      );
    }

    // Show error if network/API error occurred
    if (salesforceAccountIdCheckError) {
      return (
        <Text color={TextColor.error} variant={TextVariant.small}>
          Unable to check
        </Text>
      );
    }

    // Show checkmark if available
    if (isSalesforceAccountIdAvailable === true) {
      return (
        <img src="/assets/images/check.svg" alt="Available" data-testid="salesforce-id-valid" />
      );
    }

    // Show error icon if not available
    if (isSalesforceAccountIdAvailable === false) {
      return (
        <img
          src="/assets/images/Failed.svg"
          alt="Not Available"
          data-testid="salesforce-id-error"
        />
      );
    }

    return null;
  }, [
    enabled,
    field.value,
    originalValue,
    isSalesforceAccountIdCheckLoading,
    isSalesforceAccountIdAvailable,
    salesforceAccountIdCheckError
  ]);

  // Update form errors based on availability (only when enabled)
  useEffect(() => {
    if (!enabled) return;

    const currentValue = field.value ?? '';
    const isValid = isValidSalesforceId(currentValue);
    const matchesOriginal = originalValue !== undefined && currentValue === (originalValue ?? '');

    // Clear errors if invalid format, empty, or matches original value
    if (!isValid || !currentValue || matchesOriginal) {
      clearErrors(fieldName);
      return;
    }

    // Set error if ID is unavailable
    if (isSalesforceAccountIdAvailable === false) {
      setError(fieldName, {
        type: 'manual',
        message: 'A company with this Salesforce Account ID already exists'
      });
    } else if (isSalesforceAccountIdAvailable === true) {
      clearErrors(fieldName);
    }
  }, [
    enabled,
    isSalesforceAccountIdAvailable,
    field.value,
    originalValue,
    fieldName,
    setError,
    clearErrors
  ]);

  return {
    indicator: getIndicator(),
    isLoading: enabled ? isSalesforceAccountIdCheckLoading : false
  };
}
